import { useCallback, useEffect, useMemo, useState } from "react";
import { useTabActive } from "@/layouts/adminTabs/tabRuntime";
import { ClipboardCheck, Loader2, Package, Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchAdminOrder, fetchAdminOrderSummaries } from "@/lib/ordersApi";
import { getApiErrorMessage } from "@/lib/api";
import type { OrderDetail, OrderSummary } from "@/lib/types";
import { orderTitle } from "@/lib/orderUtils";
import { ExpeditionOrderDialog } from "@/components/admin/ExpeditionOrderDialog";
import {
  KanbanDateFilterBar,
  resolveFilterDate,
  type KanbanDateFilter,
} from "@/components/admin/KanbanDateFilter";
import { useApp } from "@/store/useApp";

const POLL_INTERVAL_MS = 15_000;

type ExpRow = OrderSummary & {
  loaded: number;
  missing: number;
  total: number;
};

type RowStatus = {
  key: "ready" | "attention" | "doing" | "todo";
  label: string;
  bar: string;
  pill: string;
  pillText: string;
};

function rowStatus(row: ExpRow): RowStatus {
  const decided = row.loaded + row.missing;
  const pending = Math.max(0, row.total - decided);
  if (row.missing > 0) {
    return {
      key: "attention",
      label: `${row.missing} em falta`,
      bar: "bg-warning",
      pill: "bg-warning/10",
      pillText: "text-warning-foreground",
    };
  }
  if (row.total > 0 && pending === 0) {
    return {
      key: "ready",
      label: "Pronto",
      bar: "bg-success",
      pill: "bg-success/10",
      pillText: "text-success-foreground",
    };
  }
  if (decided > 0) {
    return {
      key: "doing",
      label: "Conferindo",
      bar: "bg-warning",
      pill: "bg-warning/10",
      pillText: "text-warning-foreground",
    };
  }
  return {
    key: "todo",
    label: "A conferir",
    bar: "bg-muted-foreground/50",
    pill: "bg-secondary",
    pillText: "text-muted-foreground",
  };
}

export default function DashboardExpedicao() {
  // SEPARACAO só visualiza este painel — não tem permissão para conferir/alterar itens de
  // expedição, então as linhas não abrem o diálogo de conferência.
  const readOnly = useApp((s) => s.account?.role) === "SEPARACAO";
  const [rows, setRows] = useState<ExpRow[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<KanbanDateFilter>({ kind: "all" });

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) {
      setLoadState("loading");
      setError(null);
    }
    try {
      const summaries = await fetchAdminOrderSummaries();
      const inScope = summaries.filter((o) => o.status === "EM_EXPEDICAO");
      const detailed = await Promise.all(
        inScope.map(async (summary) => {
          try {
            const detail = await fetchAdminOrder(summary.id);
            const list = detail.expeditionList ?? [];
            return {
              ...summary,
              loaded: list.filter((i) => i.status === "CARREGADO").length,
              missing: list.filter((i) => i.status === "EM_FALTA").length,
              total: list.length,
            } satisfies ExpRow;
          } catch {
            return { ...summary, loaded: 0, missing: 0, total: summary.itemCount } satisfies ExpRow;
          }
        }),
      );
      setRows(detailed);
      setLoadState("ready");
    } catch (err) {
      if (!silent) {
        setError(getApiErrorMessage(err, "Não foi possível carregar os pedidos"));
        setLoadState("error");
      }
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Pausa o auto-refresh quando a aba interna está inativa (keep-alive); retoma ao reativar, sem duplicar.
  const tabActive = useTabActive();

  useEffect(() => {
    if (selectedOrderId || !tabActive) return;
    const tick = () => {
      if (document.visibilityState === "visible") void loadAll(true);
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [loadAll, selectedOrderId, tabActive]);

  const onOrderChanged = useCallback((updated: OrderDetail) => {
    setRows((current) => {
      // Saiu da expedição (liberado para rota) — remove da lista.
      if (updated.status !== "EM_EXPEDICAO") {
        return current.filter((r) => r.id !== updated.id);
      }
      const list = updated.expeditionList ?? [];
      const patch = {
        loaded: list.filter((i) => i.status === "CARREGADO").length,
        missing: list.filter((i) => i.status === "EM_FALTA").length,
        total: list.length,
      };
      return current.map((r) => (r.id === updated.id ? { ...r, ...updated, ...patch } : r));
    });
  }, []);

  const filteredRows = useMemo(() => {
    const target = resolveFilterDate(dateFilter);
    // Filtra pela data de expedição (expeditionScheduledAt); deliveryDate é só fallback.
    const expeditionDate = (r: ExpRow) => (r.expeditionScheduledAt ?? "").slice(0, 10) || r.deliveryDate;
    const scoped = target ? rows.filter((r) => expeditionDate(r) === target) : rows;
    return [...scoped].sort((a, b) =>
      (a.deliveryWindow ?? a.deliveryDate ?? "").localeCompare(b.deliveryWindow ?? b.deliveryDate ?? ""),
    );
  }, [rows, dateFilter]);

  return (
    <div>
      <header className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
        <div className="container py-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium">
            <Truck className="h-3.5 w-3.5" />
            Dashboard de expedição
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight">Pedidos em expedição</h1>
          <p className="mt-1 text-primary-foreground/80">
            Clique em um pedido para conferir os itens carregados e liberar para entrega.
          </p>
        </div>
      </header>

      <div className="container py-5">
        {loadState === "loading" && (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando pedidos...
          </div>
        )}

        {loadState === "error" && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {loadState === "ready" && (
          <>
            <div className="mb-4 flex items-center justify-between gap-3">
              <KanbanDateFilterBar value={dateFilter} onChange={setDateFilter} showAll label="Expedição:" />
              <span className="shrink-0 text-sm text-muted-foreground">
                {filteredRows.length} pedido{filteredRows.length === 1 ? "" : "s"}
              </span>
            </div>

            {filteredRows.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border/60 py-16 text-center text-sm text-muted-foreground">
                Nenhum pedido em expedição.
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border">
                {filteredRows.map((row, idx) => {
                  const st = rowStatus(row);
                  const decided = row.loaded + row.missing;
                  const pct = row.total > 0 ? Math.round((decided / row.total) * 100) : 0;
                  return (
                    <button
                      key={row.id}
                      type="button"
                      disabled={readOnly}
                      onClick={() => !readOnly && setSelectedOrderId(row.id)}
                      className={cn(
                        "flex w-full items-center gap-4 bg-surface px-4 py-3.5 text-left transition-colors",
                        readOnly ? "cursor-default" : "hover:bg-secondary/50",
                        idx > 0 && "border-t border-border",
                      )}
                    >
                      <span className={cn("h-9 w-1 shrink-0 rounded-full", st.bar)} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-display text-base font-semibold">
                            {orderTitle(row)}
                          </span>
                          <span
                            className={cn(
                              "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
                              st.pill,
                              st.pillText,
                            )}
                          >
                            {st.label}
                          </span>
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
                          <Package className="h-3.5 w-3.5" />
                          {row.itemCount} ite{row.itemCount === 1 ? "m" : "ns"}
                          {row.deliveryWindow && <span>· entrega {row.deliveryWindow}</span>}
                        </div>
                      </div>
                      <div className="hidden w-[150px] shrink-0 sm:block">
                        <div className="mb-1 flex justify-between text-xs text-muted-foreground tabular-nums">
                          <span>
                            {decided} / {row.total}
                          </span>
                          <span>{row.total > 0 ? `${pct}%` : "—"}</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                          <div className={cn("h-full rounded-full", st.bar)} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <ClipboardCheck className="h-5 w-5 shrink-0 text-muted-foreground/60" />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <ExpeditionOrderDialog
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        onOrderChanged={onOrderChanged}
      />
    </div>
  );
}
